import { Button, Icon, SvgIconNames } from '@toggle/design-system';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Option } from '~/components/filter/Option';
import { useUserUpload } from '~/views/alfred/research/useUserUpload/useUserUpload';
import { formatUploadFiles } from '~/views/alfred/research/utils/formatUploadFiles';

import * as S from './UploadDropdown.styles';

const ALLOWED_TYPES =
  '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg';

interface UploadDropdownProps {
  isMessagePending: boolean;
}

export const UploadDropdown = ({ isMessagePending }: UploadDropdownProps) => {
  const { t } = useTranslation(['chat']);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = useUserUpload(state => state.addFiles);

  const options = [
    {
      key: 'addFiles',
      icon: 'Paperclip' as SvgIconNames,
      name: t('chat:userUpload.addFiles'),
      value: {
        upload_type: 'files',
      },
    },
  ];

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      addFiles(formatUploadFiles(Array.from(files)));

      // Reset the input so the same file can be selected again
      event.target.value = '';
    }
  };

  const handleOptionClick = (optionKey: string) => {
    if (optionKey === 'addFiles') {
      handleFileClick();
    } else if (optionKey === 'addUrls') {
      // TODO: [REASON-851] Handle URL input logic
    }
  };

  return (
    <>
      <S.StyledInput
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_TYPES}
        multiple
        onChange={handleFileChange}
        disabled={isMessagePending}
      />
      <S.StyledDropdownMenu
        triggerProps={{
          disabled: isMessagePending,
        }}
        contentProps={{
          id: 'upload-dropdown-content',
        }}
        triggerNode={
          <div>
            <Button
              variant="empty"
              iconName="Add"
              size="small"
              disabled={isMessagePending}
            />
          </div>
        }
      >
        {options.map(i => {
          return (
            <Option
              key={i.key}
              option={i}
              indentedDescription={24}
              handleClick={() => handleOptionClick(i.key)}
              renderCustomFilterOptionLabel={() => {
                return (
                  <S.OptionWrapper>
                    <Icon iconName={i.icon} size={16} />
                    <S.Label>{i.name}</S.Label>
                  </S.OptionWrapper>
                );
              }}
            />
          );
        })}
      </S.StyledDropdownMenu>
    </>
  );
};
